/**
 * ModuleRegistry.jsx — M5-R4 Modules & Editions
 * Route: /setup/module-registry
 * Authorization: institution_admin or super_admin
 *
 * UI: Salesforce-style enterprise configuration workspace.
 * - Edition Selection (BASIC / STANDARD / PROFESSIONAL / ENTERPRISE)
 * - Module Catalog grouped by 00–10 canonical taxonomy
 * - Activation Plan drawer (dependency preview before action)
 * - Readiness panel (from SS03 — no second readiness engine)
 *
 * Invariants:
 *   - LEGACY_ACTIVE modules are shown with protection badge; admin may disable if optional.
 *   - Dependency calculation is ALWAYS in the backend.
 *   - Edition change DOES NOT auto-activate any module.
 *   - No M6 history tab here.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Circle,
  XCircle,
  Layers,
  Zap,
  Shield,
  Info,
  ArrowRight,
  Loader2,
  Lock,
  X,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Edition metadata ─────────────────────────────────────────────────────────

const EDITION_META = {
  BASIC:        { color: "border-slate-300 bg-white",         accent: "text-slate-700",  badge: "bg-slate-100 text-slate-700 border-slate-200",  ring: "ring-slate-400"  },
  STANDARD:     { color: "border-blue-300 bg-blue-50/30",     accent: "text-blue-700",   badge: "bg-blue-100 text-blue-700 border-blue-200",     ring: "ring-blue-500"   },
  PROFESSIONAL: { color: "border-indigo-300 bg-indigo-50/30", accent: "text-indigo-700", badge: "bg-indigo-100 text-indigo-700 border-indigo-200", ring: "ring-indigo-500" },
  ENTERPRISE:   { color: "border-violet-300 bg-violet-50/30", accent: "text-violet-700", badge: "bg-violet-100 text-violet-700 border-violet-200", ring: "ring-violet-500" },
};

const EDITION_DESCRIPTION = {
  BASIC:        "Core institutional management — student records, basic academics, and essential reporting.",
  STANDARD:     "Adds assessment management, compliance tooling, and structured curriculum frameworks.",
  PROFESSIONAL: "Full evaluation lifecycle, research support, advanced finance, and HCM capabilities.",
  ENTERPRISE:   "Complete platform access — institutional intelligence, all specialized modules, and API integrations.",
};

// ── Build state badges ───────────────────────────────────────────────────────

function BuildBadge({ state }) {
  const map = {
    LIVE:        "bg-emerald-50 text-emerald-700 border-emerald-200",
    BETA:        "bg-blue-50 text-blue-700 border-blue-200",
    PLANNED:     "bg-slate-100 text-slate-500 border-slate-200",
    DEPRECATED:  "bg-amber-50 text-amber-700 border-amber-200",
  };
  const cls = map[state] || map.PLANNED;
  return (
    <span className={`inline-flex text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
      {state}
    </span>
  );
}

function ActivationStatusBadge({ status, reason }) {
  if (!status || status === "NOT_CONFIGURED") {
    return <span className="text-[10px] text-slate-400">—</span>;
  }
  const map = {
    ACTIVE:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    INACTIVE: "bg-slate-100 text-slate-500 border-slate-200",
    SUSPENDED:"bg-amber-50 text-amber-700 border-amber-200",
  };
  const cls = map[status] || map.INACTIVE;
  const isLegacy = reason === "LEGACY_ACTIVE";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
      {isLegacy && <Lock className="h-2.5 w-2.5" />}
      {status}
    </span>
  );
}

// ── Typed error messages in plain language ────────────────────────────────────

function humanizeError(errorCode, message, detail) {
  const dependents = detail?.active_dependents || [];
  const notEntitled = detail?.not_entitled || [];

  switch (errorCode) {
    case "MODULE_MANDATORY":
      return "This module is mandatory in your current edition and cannot be disabled.";
    case "MODULE_REQUIRED_BY_ACTIVE_DEPENDENT":
      return `This module is required by: ${dependents.join(", ")}. Disable those first.`;
    case "MODULE_NOT_ENTITLED":
      return "This module is not included in your current edition. Upgrade your edition to enable it.";
    case "DEPENDENCY_NOT_ENTITLED":
      return `Required dependency modules are not in your current edition: ${notEntitled.join(", ")}.`;
    case "MODULE_NOT_FOUND":
      return "Module not found in the registry.";
    case "DEPENDENCY_NOT_READY":
      return "A required dependency module is not ready. Complete its setup first.";
    case "INVALID_STATE_TRANSITION":
      return message || "This state transition is not permitted.";
    case "CROSS_INSTITUTION_ACCESS":
      return "Access denied.";
    default:
      return message || "An unexpected error occurred.";
  }
}

// ── Readiness status indicator ────────────────────────────────────────────────

function ReadinessStatus({ status }) {
  if (status === "READY")
    return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>;
  if (status === "BLOCKED")
    return <span className="inline-flex items-center gap-1 text-xs text-red-500"><XCircle className="h-3.5 w-3.5" /> Blocked</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Circle className="h-3.5 w-3.5" /> {status || "—"}</span>;
}

// ── Edition Card ──────────────────────────────────────────────────────────────

function EditionCard({ edition, isCurrent, onSelect, loading }) {
  const meta = EDITION_META[edition.edition_code] || EDITION_META.BASIC;
  const desc = EDITION_DESCRIPTION[edition.edition_code] || "";
  return (
    <button
      data-testid={`edition-card-${edition.edition_code}`}
      onClick={() => !isCurrent && onSelect(edition.edition_code)}
      disabled={loading}
      className={`
        relative w-full text-left rounded-xl border-2 p-4 transition-all duration-150
        ${meta.color}
        ${isCurrent ? `${meta.ring} ring-2 shadow-sm` : "hover:shadow-md hover:-translate-y-0.5"}
        ${loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        focus-visible:outline-none focus-visible:ring-2
      `}
    >
      {isCurrent && (
        <span className={`absolute top-2.5 right-2.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.badge}`}>
          Current
        </span>
      )}
      <div className={`text-sm font-bold mb-0.5 ${meta.accent}`}>
        {edition.edition_name}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mb-3">{desc}</p>
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span><span className="font-semibold text-slate-700">{edition.module_count}</span> modules</span>
        <span><span className="font-semibold text-slate-700">{edition.mandatory_count}</span> mandatory</span>
      </div>
    </button>
  );
}

// ── Activation Plan Drawer ────────────────────────────────────────────────────

function ActivationDrawer({ module, plan, planLoading, planError, onActivate, onDisable, activating, disabling, institutionModule, onClose }) {
  const isActive = institutionModule?.status === "ACTIVE";
  const isLegacy = institutionModule?.activation_reason === "LEGACY_ACTIVE";
  const isMandatory = false; // rendered from plan if blocked

  return (
    <div
      data-testid="activation-drawer"
      className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl border-l border-slate-200 z-40 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-mono text-slate-400">{module.module_code}</span>
            <BuildBadge state={module.build_state} />
          </div>
          <h2 className="text-base font-semibold text-slate-900 leading-snug">
            {module.module_name}
          </h2>
        </div>
        <button
          data-testid="drawer-close-btn"
          onClick={onClose}
          className="ml-3 p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

        {/* Current status */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Current status:</span>
          <ActivationStatusBadge status={institutionModule?.status || "NOT_CONFIGURED"} reason={institutionModule?.activation_reason} />
          {isLegacy && (
            <span className="text-[10px] text-amber-600 flex items-center gap-1">
              <Lock className="h-2.5 w-2.5" /> Legacy import
            </span>
          )}
        </div>

        {/* Plan loading */}
        {planLoading && (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            Computing activation plan…
          </div>
        )}

        {/* Plan error */}
        {planError && !planLoading && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{planError}</p>
          </div>
        )}

        {/* Plan content */}
        {plan && !planLoading && (
          <>
            {/* Already active */}
            {plan.already_active && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-700 font-medium">This module is already active.</p>
              </div>
            )}

            {/* Not entitled */}
            {!plan.entitled && plan.not_entitled?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1">
                <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Not entitled under current edition
                </p>
                {plan.not_entitled.map(c => (
                  <p key={c} className="text-xs text-amber-600">· {c}</p>
                ))}
                <p className="text-xs text-amber-600 mt-1">Upgrade your edition to enable this module.</p>
              </div>
            )}

            {/* Dependencies to activate */}
            {plan.to_activate?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Will also activate ({plan.to_activate.filter(t => t.dependency_type !== "TARGET").length} required dependencies)
                </p>
                <div className="space-y-1.5">
                  {plan.to_activate.filter(t => t.dependency_type !== "TARGET").map(dep => (
                    <div key={dep.module_code} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                      <ArrowRight className="h-3 w-3 text-blue-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs font-medium text-slate-700">{dep.module_name}</span>
                        <span className="ml-1.5 text-[10px] font-mono text-slate-400">{dep.module_code}</span>
                      </div>
                      <span className="ml-auto text-[10px] text-blue-600 font-medium">REQUIRED</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edition code context */}
            {plan.edition_code && (
              <p className="text-xs text-slate-400">
                Edition context: <span className="font-medium text-slate-600">{plan.edition_code}</span>
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-4 border-t border-slate-100 space-y-2">
        {!isActive && plan && plan.entitled && !plan.already_active && (
          <button
            data-testid="drawer-activate-btn"
            onClick={onActivate}
            disabled={activating}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            {activating
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Activating…</>
              : <><Zap className="h-4 w-4" /> Enable with required modules</>
            }
          </button>
        )}

        {isActive && (
          <button
            data-testid="drawer-disable-btn"
            onClick={onDisable}
            disabled={disabling || isLegacy && false}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-colors disabled:opacity-60"
          >
            {disabling
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Disabling…</>
              : <><XCircle className="h-4 w-4" /> Disable this module</>
            }
          </button>
        )}

        {plan && !plan.entitled && (
          <p className="text-xs text-center text-slate-400">
            Upgrade your edition to enable this module.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Module Card ───────────────────────────────────────────────────────────────

function ModuleCard({ module, institutionModule, isEntitled, readinessMap, onOpen }) {
  const status = institutionModule?.status;
  const reason = institutionModule?.activation_reason;
  const readiness = readinessMap?.[module.module_code];

  return (
    <button
      data-testid={`module-card-${module.module_code}`}
      onClick={() => onOpen(module)}
      className={`
        group w-full text-left rounded-lg border p-3 transition-all duration-100
        hover:shadow-sm hover:border-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400
        ${status === "ACTIVE" ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}
      `}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span className="text-[10px] font-mono text-slate-400">{module.module_code}</span>
            <BuildBadge state={module.build_state} />
            {!isEntitled && (
              <span className="text-[10px] text-amber-600 border border-amber-200 bg-amber-50 px-1 py-0.5 rounded">
                Not in edition
              </span>
            )}
          </div>
          <p className="text-xs font-semibold text-slate-800 leading-snug group-hover:text-blue-700 transition-colors">
            {module.module_name}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <ActivationStatusBadge status={status} reason={reason} />
          {readiness && (
            <ReadinessStatus status={readiness.overall_ready ? "READY" : "BLOCKED"} />
          )}
        </div>
      </div>
      {module.capability_count > 0 && (
        <p className="mt-1.5 text-[10px] text-slate-400">
          {module.capability_count} {module.capability_count === 1 ? "capability" : "capabilities"}
        </p>
      )}
    </button>
  );
}

// ── Module Group Accordion ────────────────────────────────────────────────────

function ModuleGroup({ group, institutionModulesMap, entitlementSet, readinessMap, onModuleOpen }) {
  const [open, setOpen] = useState(true);
  const activeCount = group.modules.filter(m => institutionModulesMap[m.module_code]?.status === "ACTIVE").length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`module-group-${group.group_code}`}>
      <button
        data-testid={`group-toggle-${group.group_code}`}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-slate-400 shrink-0">{group.group_code}</span>
          <span className="text-sm font-semibold text-slate-800">{group.group_name}</span>
          {activeCount > 0 && (
            <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
              {activeCount} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{group.modules.length}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {group.modules.map(m => (
            <ModuleCard
              key={m.module_code}
              module={m}
              institutionModule={institutionModulesMap[m.module_code]}
              isEntitled={entitlementSet === null || entitlementSet.has(m.module_code)}
              readinessMap={readinessMap}
              onOpen={onModuleOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Readiness Panel ───────────────────────────────────────────────────────────

function ReadinessPanel({ readiness, loading, error, navigate }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading readiness…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
        <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
      </div>
    );
  }
  if (!readiness || !readiness.modules?.length) {
    return (
      <div className="py-8 text-center text-sm text-slate-400">
        No readiness requirements found for active modules.
        <br />
        <span className="text-xs">Activate modules to see their readiness gates.</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${readiness.all_ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        {readiness.all_ready
          ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          : <AlertTriangle className="h-5 w-5 text-amber-500" />
        }
        <div>
          <p className="text-sm font-semibold text-slate-800">
            {readiness.all_ready ? "All modules ready" : "Action required on some modules"}
          </p>
          <p className="text-xs text-slate-500">{readiness.modules.length} modules evaluated</p>
        </div>
        <button
          data-testid="readiness-goto-gates-btn"
          onClick={() => navigate("/setup/gates")}
          className="ml-auto flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
        >
          Setup Gates <ExternalLink className="h-3 w-3" />
        </button>
      </div>

      {/* Per-module */}
      {readiness.modules.map(mod => (
        <div key={mod.module_code} data-testid={`readiness-row-${mod.module_code}`}
             className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-slate-400">{mod.module_code}</span>
              <span className="text-sm font-medium text-slate-800">{mod.module_name}</span>
            </div>
            <ReadinessStatus status={mod.overall_ready ? "READY" : "BLOCKED"} />
          </div>

          {!mod.overall_ready && mod.requirements?.length > 0 && (
            <div className="border-t border-slate-100 px-4 py-2 space-y-1">
              {mod.requirements
                .filter(r => !r.status || r.status === "BLOCKED")
                .map((req, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                    <span>
                      {req.reference_label || req.reference_code}
                      {req.is_blocking && <span className="ml-1 text-red-500 font-semibold">(blocking)</span>}
                    </span>
                  </div>
                ))}
              <button
                onClick={() => navigate("/setup/gates")}
                className="mt-1 text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                Go to Setup Studio <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Confirm Edition Change Modal ──────────────────────────────────────────────

function EditionConfirmModal({ from, to, onConfirm, onCancel, loading }) {
  const isDowngrade = () => {
    const order = ["BASIC", "STANDARD", "PROFESSIONAL", "ENTERPRISE"];
    return order.indexOf(to) < order.indexOf(from);
  };
  const downgrade = isDowngrade();

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edition-confirm-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          {downgrade
            ? <AlertTriangle className="h-6 w-6 text-amber-500 shrink-0 mt-0.5" />
            : <Info className="h-6 w-6 text-blue-500 shrink-0 mt-0.5" />
          }
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {downgrade ? "Confirm edition downgrade" : "Confirm edition change"}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {from ? `Changing from ${from}` : "Setting edition"} to <strong>{to}</strong>.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1 mb-5">
          <p><strong>Entitlements</strong> will change to reflect the {to} module set.</p>
          <p><strong>No modules will be activated or deactivated</strong> automatically.</p>
          {downgrade && <p className="text-amber-700"><strong>Downgrade note:</strong> Some currently-entitled modules may no longer be entitled. Existing active modules are not affected unless you manually disable them.</p>}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={loading}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-60">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading} data-testid="edition-confirm-apply-btn"
                  className={`px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-60 ${downgrade ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}`}>
            {loading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Applying…</span> : `Apply ${to}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ModuleRegistry() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Data state ─────────────────────────────────────────────────────────────
  const [editions, setEditions]           = useState([]);
  const [catalog, setCatalog]             = useState([]);
  const [institutionData, setInstData]    = useState(null);  // {institution_id, current_edition_code, modules}
  const [readiness, setReadiness]         = useState(null);
  const [entitlementSet, setEntitlementSet] = useState(null); // Set<module_code> or null (unfiltered)

  // ── Loading state ──────────────────────────────────────────────────────────
  const [loadingEditions, setLoadingEditions] = useState(true);
  const [loadingCatalog, setLoadingCatalog]   = useState(true);
  const [loadingInst, setLoadingInst]         = useState(true);
  const [loadingReadiness, setLoadingReadiness] = useState(true);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]         = useState("modules");
  const [selectedModule, setSelectedModule] = useState(null);
  const [activationPlan, setActivationPlan] = useState(null);
  const [planLoading, setPlanLoading]     = useState(false);
  const [planError, setPlanError]         = useState(null);
  const [activating, setActivating]       = useState(false);
  const [disabling, setDisabling]         = useState(false);
  const [pendingEdition, setPendingEdition] = useState(null);
  const [settingEdition, setSettingEdition] = useState(false);

  const institutionId = user?.institution_id;

  // ── Resolve institution_id for super_admin (mirrors SetupStudio.jsx pattern) ─
  // super_admin JWT has institution_id=null; resolve from /api/setup/domains context.
  const [resolvedInstitutionId, setResolvedInstitutionId] = useState(institutionId);

  useEffect(() => {
    if (institutionId) { setResolvedInstitutionId(institutionId); return; }
    if (user?.role === "super_admin") {
      fetch(`${API}/api/setup/domains`, { headers: authHeaders() })
        .then(r => r.json())
        .then(d => { if (d.institution_id) setResolvedInstitutionId(d.institution_id); })
        .catch(() => {});
    }
  }, [institutionId, user?.role]); // eslint-disable-line

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const fetchEditions = useCallback(async () => {
    setLoadingEditions(true);
    try {
      const r = await fetch(`${API}/api/modules/editions`, { headers: authHeaders() });
      if (r.ok) setEditions(await r.json());
    } finally {
      setLoadingEditions(false);
    }
  }, []); // eslint-disable-line

  const fetchCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const r = await fetch(`${API}/api/modules/catalog`, { headers: authHeaders() });
      if (r.ok) setCatalog(await r.json());
    } finally {
      setLoadingCatalog(false);
    }
  }, []); // eslint-disable-line

  const fetchInstitution = useCallback(async () => {
    if (!resolvedInstitutionId && user?.role !== "super_admin") return;
    setLoadingInst(true);
    try {
      const url = `${API}/api/modules/institution${user?.role === "super_admin" && resolvedInstitutionId ? `?institution_id=${resolvedInstitutionId}` : ""}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setInstData(data);
        // Load entitlements for current edition
        if (data.current_edition_code) {
          await loadEditionEntitlements(data.current_edition_code);
        } else {
          setEntitlementSet(null);
        }
      }
    } finally {
      setLoadingInst(false);
    }
  }, [resolvedInstitutionId, user?.role]); // eslint-disable-line

  const loadEditionEntitlements = async (editionCode) => {
    if (!editionCode) { setEntitlementSet(null); return; }
    try {
      const r = await fetch(`${API}/api/modules/editions?edition_code=${editionCode}`, { headers: authHeaders() });
      if (r.ok) {
        const rows = await r.json();
        setEntitlementSet(new Set(rows.map(e => e.module_code)));
      }
    } catch { /* non-fatal */ }
  };

  const fetchReadiness = useCallback(async () => {
    if (!resolvedInstitutionId) return;
    setLoadingReadiness(true);
    try {
      const url = `${API}/api/modules/readiness${user?.role === "super_admin" && resolvedInstitutionId ? `?institution_id=${resolvedInstitutionId}` : ""}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) setReadiness(await r.json());
    } finally {
      setLoadingReadiness(false);
    }
  }, [resolvedInstitutionId, user?.role]); // eslint-disable-line

  useEffect(() => {
    fetchEditions();
    fetchCatalog();
    fetchInstitution();
    fetchReadiness();
  }, [fetchEditions, fetchCatalog, fetchInstitution, fetchReadiness]);

  // ── Build institution module map ───────────────────────────────────────────

  const institutionModulesMap = {};
  (institutionData?.modules || []).forEach(m => {
    institutionModulesMap[m.module_code] = m;
  });

  const readinessMap = {};
  (readiness?.modules || []).forEach(m => {
    readinessMap[m.module_code] = m;
  });

  // ── Open module drawer ─────────────────────────────────────────────────────

  const openModule = async (module) => {
    setSelectedModule(module);
    setActivationPlan(null);
    setPlanError(null);
    setPlanLoading(true);

    try {
      const editionParam = institutionData?.current_edition_code
        ? `?edition_code=${institutionData.current_edition_code}`
        : "";
      const instQ = resolvedInstitutionId && user?.role === "super_admin"
        ? `${editionParam ? "&" : "?"}institution_id=${resolvedInstitutionId}`
        : "";

      const url = `${API}/api/modules/activation-plan/${module.module_code}${editionParam}${instQ}`;
      const r = await fetch(url, { headers: authHeaders() });
      const data = await r.json();
      if (!r.ok) {
        setPlanError(humanizeError(data.code, data.message, data));
      } else {
        setActivationPlan(data);
      }
    } catch (e) {
      setPlanError("Failed to load activation plan: " + e.message);
    } finally {
      setPlanLoading(false);
    }
  };

  // ── Activate with deps ─────────────────────────────────────────────────────

  const handleActivate = async () => {
    if (!selectedModule) return;
    setActivating(true);
    try {
      const instQ = resolvedInstitutionId && user?.role === "super_admin" ? `?institution_id=${resolvedInstitutionId}` : "";
      const r = await fetch(
        `${API}/api/modules/activate-with-dependencies/${selectedModule.module_code}${instQ}`,
        {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ edition_code: institutionData?.current_edition_code || null }),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(humanizeError(data.code, data.message, data));
      } else {
        toast.success(`${selectedModule.module_name} enabled${data.activated?.length > 1 ? ` (+ ${data.activated.length - 1} dependencies)` : ""}.`);
        await fetchInstitution();
        await fetchReadiness();
        // Re-fetch plan to update drawer
        await openModule(selectedModule);
      }
    } catch (e) {
      toast.error("Activation failed: " + e.message);
    } finally {
      setActivating(false);
    }
  };

  // ── Disable module ─────────────────────────────────────────────────────────

  const handleDisable = async () => {
    if (!selectedModule) return;
    setDisabling(true);
    try {
      const instQ = resolvedInstitutionId && user?.role === "super_admin" ? `?institution_id=${resolvedInstitutionId}` : "";
      const r = await fetch(
        `${API}/api/modules/${selectedModule.module_code}${instQ}`,
        {
          method: "PATCH",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ action: "disable" }),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(humanizeError(data.code || data.detail?.code, data.message || data.detail?.message, data.detail || data));
      } else {
        toast.success(`${selectedModule.module_name} disabled.`);
        await fetchInstitution();
        await fetchReadiness();
        await openModule(selectedModule);
      }
    } catch (e) {
      toast.error("Disable failed: " + e.message);
    } finally {
      setDisabling(false);
    }
  };

  // ── Set edition ────────────────────────────────────────────────────────────

  const handleEditionSelect = (code) => {
    setPendingEdition(code);
  };

  const applyEditionChange = async () => {
    if (!pendingEdition) return;
    setSettingEdition(true);
    try {
      const instQ = resolvedInstitutionId && user?.role === "super_admin" ? `?institution_id=${resolvedInstitutionId}` : "";
      const r = await fetch(
        `${API}/api/modules/institution/edition${instQ}`,
        {
          method: "PATCH",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ edition_code: pendingEdition }),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        toast.error(humanizeError(data.code, data.message, data));
      } else {
        toast.success(`Edition set to ${pendingEdition}. No modules were changed.`);
        setPendingEdition(null);
        await fetchInstitution();
      }
    } catch (e) {
      toast.error("Edition change failed: " + e.message);
    } finally {
      setSettingEdition(false);
    }
  };

  // ── Summary stats ──────────────────────────────────────────────────────────

  const activeModules  = (institutionData?.modules || []).filter(m => m.status === "ACTIVE").length;
  const totalCatalog   = catalog.reduce((s, g) => s + g.modules.length, 0);
  const currentEdition = institutionData?.current_edition_code;

  const loading = loadingEditions || loadingCatalog || (loadingInst && !!resolvedInstitutionId);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50" data-testid="module-registry-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="back-to-setup-btn"
                onClick={() => navigate("/setup")}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors mr-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Setup Studio
              </button>
              <div className="w-px h-5 bg-slate-200" />
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
                <Layers className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900" data-testid="module-registry-title">
                  Modules &amp; Editions
                </h1>
                <p className="text-xs text-slate-500">AcademiaOS Platform Capability Configuration</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Institution context */}
              <div className="text-right hidden sm:block" data-testid="institution-context">
                <p className="text-xs font-medium text-slate-700">{user?.institution_name || "Institution"}</p>
                {currentEdition && (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${EDITION_META[currentEdition]?.badge || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {currentEdition}
                  </span>
                )}
              </div>
              <button
                data-testid="refresh-all-btn"
                onClick={() => { fetchEditions(); fetchCatalog(); fetchInstitution(); fetchReadiness(); }}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab bar ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white px-6" data-testid="module-registry-tabs">
        <div className="max-w-6xl mx-auto flex items-center gap-0">
          {[
            { key: "modules",   label: "Modules" },
            { key: "readiness", label: "Readiness" },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* ── Modules Tab ─────────────────────────────────────────────────── */}
        {activeTab === "modules" && (
          <div className="space-y-6">

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3" data-testid="module-summary-stats">
              {[
                { label: "Active Modules",   value: activeModules,   cls: "text-emerald-600" },
                { label: "Catalog Size",      value: totalCatalog,    cls: "text-slate-700"   },
                { label: "Current Edition",   value: currentEdition || "Not set", cls: currentEdition ? EDITION_META[currentEdition]?.accent : "text-slate-400" },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
                  <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Edition selector */}
            <section data-testid="edition-section">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Edition</h2>
                <div className="flex-1 h-px bg-slate-200" />
                {!currentEdition && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> No edition assigned
                  </span>
                )}
              </div>
              {loadingEditions ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-xl border border-slate-200 bg-white animate-pulse" />)}
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {editions.map(ed => (
                    <EditionCard
                      key={ed.edition_code}
                      edition={ed}
                      isCurrent={ed.edition_code === currentEdition}
                      onSelect={handleEditionSelect}
                      loading={settingEdition}
                    />
                  ))}
                </div>
              )}
              {currentEdition && (
                <p className="mt-2 text-xs text-slate-400">
                  <Shield className="h-3 w-3 inline mr-1" />
                  Changing edition updates entitlements only. No modules are activated or deactivated automatically.
                </p>
              )}
            </section>

            {/* Module catalog */}
            <section data-testid="module-catalog-section">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Module Catalog</h2>
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">{totalCatalog} modules · {catalog.length} domains</span>
              </div>

              {loadingCatalog ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => <div key={i} className="h-24 rounded-xl border border-slate-200 bg-white animate-pulse" />)}
                </div>
              ) : (
                <div className="space-y-3">
                  {catalog.map(group => (
                    <ModuleGroup
                      key={group.group_code}
                      group={group}
                      institutionModulesMap={institutionModulesMap}
                      entitlementSet={entitlementSet}
                      readinessMap={readinessMap}
                      onModuleOpen={openModule}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* ── Readiness Tab ────────────────────────────────────────────────── */}
        {activeTab === "readiness" && (
          <div data-testid="readiness-tab-content">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Module Readiness</h2>
              <div className="flex-1 h-px bg-slate-200" />
              <p className="text-xs text-slate-400">Delegated to SS03 — no second readiness engine</p>
            </div>
            <ReadinessPanel
              readiness={readiness}
              loading={loadingReadiness}
              error={null}
              navigate={navigate}
            />
          </div>
        )}
      </div>

      {/* ── Activation Drawer ────────────────────────────────────────────────── */}
      {selectedModule && (
        <>
          <div
            data-testid="drawer-backdrop"
            className="fixed inset-0 bg-black/20 z-30"
            onClick={() => setSelectedModule(null)}
          />
          <ActivationDrawer
            module={selectedModule}
            plan={activationPlan}
            planLoading={planLoading}
            planError={planError}
            onActivate={handleActivate}
            onDisable={handleDisable}
            activating={activating}
            disabling={disabling}
            institutionModule={institutionModulesMap[selectedModule.module_code]}
            onClose={() => setSelectedModule(null)}
          />
        </>
      )}

      {/* ── Edition Confirm Modal ─────────────────────────────────────────── */}
      {pendingEdition && (
        <EditionConfirmModal
          from={currentEdition}
          to={pendingEdition}
          onConfirm={applyEditionChange}
          onCancel={() => setPendingEdition(null)}
          loading={settingEdition}
        />
      )}
    </div>
  );
}
